import { FC, useEffect, useMemo, useState } from 'react'

import { Button, Input, SimpleSelect } from 'components/atoms'

type SLAType = 'DOT_Hours' | 'DOT_Cutoff' | 'DOT_Dispatch'

type SelectOption = {
  label: string
  value?: string
  disabled?: boolean
}

export interface PartnerSLAItem {
  id: string
  type: SLAType
  partnerCode?: string
  orderType: string
  hours?: number
  startTime?: string
  endTime?: string
  dispatchDays?: number
  updatedAt?: string
  updatedBy?: string
}

export interface PartnerSLAProps {
  value?: PartnerSLAItem[]
  onChange?: (items: PartnerSLAItem[]) => void
  orderTypeOptions?: SelectOption[]
  className?: string
}

const ALL_ORDER_TYPE = 'All'

const timeOptions: SelectOption[] = [
  { label: '12.00.00 AM', value: '12.00.00 AM' },
  { label: '01.00.00 AM', value: '01.00.00 AM' },
  { label: '02.00.00 AM', value: '02.00.00 AM' },
  { label: '03.00.00 AM', value: '03.00.00 AM' },
  { label: '04.00.00 AM', value: '04.00.00 AM' },
  { label: '05.00.00 AM', value: '05.00.00 AM' },
  { label: '06.00.00 AM', value: '06.00.00 AM' },
  { label: '07.00.00 AM', value: '07.00.00 AM' },
  { label: '08.00.00 AM', value: '08.00.00 AM' },
  { label: '09.00.00 AM', value: '09.00.00 AM' },
  { label: '10.00.00 AM', value: '10.00.00 AM' },
  { label: '11.00.00 AM', value: '11.00.00 AM' },
  { label: '12.00.00 PM', value: '12.00.00 PM' },
  { label: '01.00.00 PM', value: '01.00.00 PM' },
  { label: '02.00.00 PM', value: '02.00.00 PM' },
  { label: '03.00.00 PM', value: '03.00.00 PM' },
  { label: '04.00.00 PM', value: '04.00.00 PM' },
  { label: '05.00.00 PM', value: '05.00.00 PM' },
  { label: '06.00.00 PM', value: '06.00.00 PM' },
  { label: '07.00.00 PM', value: '07.00.00 PM' },
  { label: '08.00.00 PM', value: '08.00.00 PM' },
  { label: '09.00.00 PM', value: '09.00.00 PM' },
  { label: '10.00.00 PM', value: '10.00.00 PM' },
  { label: '11.00.00 PM', value: '11.00.00 PM' },
  { label: '11.59.59 PM', value: '11.59.59 PM' },
]

const DEFAULT_CUTOFF_START = timeOptions[0]
const DEFAULT_CUTOFF_END = timeOptions[24]

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
const getSelectValue = (option: SelectOption | null) =>
  option?.label || option?.value || ''
const isNumericString = (value: string) => /^\d*$/.test(value)

const formatUpdatedText = (item: PartnerSLAItem) => {
  if (item.updatedAt && item.updatedBy) {
    return `Updated ${item.updatedAt} - ${item.updatedBy}`
  }

  if (item.updatedAt) {
    return `Updated ${item.updatedAt}`
  }

  if (item.updatedBy) {
    return `Updated - ${item.updatedBy}`
  }

  return ''
}

const getTimeIndex = (time: string) =>
  timeOptions.findIndex(
    (option) => option.value === time || option.label === time,
  )

const isCutoffOverlapping = (
  existingItems: PartnerSLAItem[],
  newOrderType: string,
  newStart: string,
  newEnd: string,
) => {
  const newStartIndex = getTimeIndex(newStart)
  const newEndIndex = getTimeIndex(newEnd)

  if (newStartIndex === -1 || newEndIndex === -1) return true
  if (newStartIndex > newEndIndex) return true

  const sameOrderTypeCutoffs = existingItems.filter(
    (item) => item.type === 'DOT_Cutoff' && item.orderType === newOrderType,
  )

  return sameOrderTypeCutoffs.some((item) => {
    const existingStartIndex = getTimeIndex(item.startTime || '')
    const existingEndIndex = getTimeIndex(item.endTime || '')

    if (existingStartIndex === -1 || existingEndIndex === -1) return true

    return newStartIndex < existingEndIndex && newEndIndex > existingStartIndex
  })
}

const DeleteButton: FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type='button'
    onClick={onClick}
    className='text-R-500 hover:text-R-600 text-lg flex items-center justify-center'
    aria-label='Delete'>
    <i className='ri-delete-bin-6-line'></i>
  </button>
)

const SectionCard: FC<{
  title: string
  children: React.ReactNode
}> = ({ title, children }) => (
  <section className='rounded-md border border-N-200 p-4'>
    <h3 className='mb-4 text-lg font-medium'>{title}</h3>
    {children}
  </section>
)

export const PartnerSLA: FC<PartnerSLAProps> = ({
  value = [],
  onChange,
  orderTypeOptions = [],
  className = '',
}) => {
  const [items, setItems] = useState<PartnerSLAItem[]>(value)

  const [hoursOrderType, setHoursOrderType] = useState<SelectOption | null>(
    null,
  )
  const [hoursValue, setHoursValue] = useState('')
  const [hoursError, setHoursError] = useState('')

  const [cutoffOrderType, setCutoffOrderType] = useState<SelectOption | null>(
    null,
  )
  const [cutoffStart, setCutoffStart] =
    useState<SelectOption>(DEFAULT_CUTOFF_START)
  const [cutoffEnd, setCutoffEnd] = useState<SelectOption>(DEFAULT_CUTOFF_END)
  const [dispatchDays, setDispatchDays] = useState('0')
  const [cutoffError, setCutoffError] = useState('')

  const [dispatchOrderType, setDispatchOrderType] =
    useState<SelectOption | null>(null)
  const [dispatchError, setDispatchError] = useState('')

  useEffect(() => {
    setItems(value ?? [])
  }, [value])

  const hoursRows = useMemo(
    () => items.filter((item) => item.type === 'DOT_Hours'),
    [items],
  )

  const cutoffRows = useMemo(
    () => items.filter((item) => item.type === 'DOT_Cutoff'),
    [items],
  )

  const dispatchRows = useMemo(
    () => items.filter((item) => item.type === 'DOT_Dispatch'),
    [items],
  )

  const hasAllConfiguration = useMemo(
    () => items.some((item) => item.orderType === ALL_ORDER_TYPE),
    [items],
  )

  const hasSpecificConfiguration = useMemo(
    () =>
      items.some(
        (item) =>
          item.orderType &&
          item.orderType !== 'Select' &&
          item.orderType !== ALL_ORDER_TYPE,
      ),
    [items],
  )

  const availableOrderTypes = useMemo(() => {
    const hasAllOption = orderTypeOptions.some(
      (item) => item.label === ALL_ORDER_TYPE,
    )

    return hasAllOption
      ? orderTypeOptions
      : [
          { label: ALL_ORDER_TYPE, value: ALL_ORDER_TYPE, disabled: false },
          ...orderTypeOptions,
        ]
  }, [orderTypeOptions])

  const addItem = (item: PartnerSLAItem) => {
    setItems((prev) => {
      let next: PartnerSLAItem[]

      if (item.type === 'DOT_Cutoff') {
        next = [...prev, item]
      } else {
        next = [
          ...prev.filter(
            (existing) =>
              !(
                existing.type === item.type &&
                existing.orderType === item.orderType
              ),
          ),
          item,
        ]
      }

      onChange?.(next)
      return next
    })
  }

  const clearErrors = () => {
    setHoursError('')
    setCutoffError('')
    setDispatchError('')
  }

  const removeItem = (id: string) => {
    setItems((prev) => {
      const next = prev.filter((item) => item.id !== id)
      onChange?.(next)
      return next
    })
    clearErrors()
  }

  const handleAddHours = () => {
    if (!hoursOrderType || hoursValue === '') return

    const parsedHours = parseInt(hoursValue, 10)
    if (Number.isNaN(parsedHours) || parsedHours < 0) return

    const selectedOrderType = getSelectValue(hoursOrderType)

    if (!selectedOrderType || selectedOrderType === 'Select') {
      setHoursError('Please select a valid order type.')
      return
    }

    if (selectedOrderType === ALL_ORDER_TYPE && hasSpecificConfiguration) {
      setHoursError(
        'Please remove type specific configuration to add all type.',
      )
      return
    }

    if (selectedOrderType !== ALL_ORDER_TYPE && hasAllConfiguration) {
      setHoursError('Please remove all type configuration to add type specific')
      return
    }

    const alreadyExistsInOtherCategories = items.some(
      (item) =>
        (item.type === 'DOT_Cutoff' || item.type === 'DOT_Dispatch') &&
        item.orderType === selectedOrderType,
    )

    if (alreadyExistsInOtherCategories) {
      setHoursError('SLA already configured for Order type.')
      return
    }

    setHoursError('')

    addItem({
      id: makeId(),
      type: 'DOT_Hours',
      orderType: selectedOrderType,
      hours: parsedHours,
    })

    setHoursOrderType(null)
    setHoursValue('')
  }

  const handleAddCutoff = () => {
    if (!cutoffOrderType) return

    const selectedOrderType = getSelectValue(cutoffOrderType)
    const selectedStart = getSelectValue(cutoffStart)
    const selectedEnd = getSelectValue(cutoffEnd)

    const startIndex = getTimeIndex(selectedStart)
    const endIndex = getTimeIndex(selectedEnd)

    if (startIndex === -1 || endIndex === -1) return

    if (!selectedOrderType || selectedOrderType === 'Select') {
      setCutoffError('Please select a valid order type.')
      return
    }

    if (selectedOrderType === ALL_ORDER_TYPE && hasSpecificConfiguration) {
      setCutoffError(
        'Please remove type specific configuration to add all type.',
      )
      return
    }

    if (selectedOrderType !== ALL_ORDER_TYPE && hasAllConfiguration) {
      setCutoffError(
        'Please remove all type configuration to add type specific',
      )
      return
    }

    if (startIndex > endIndex) {
      setCutoffError('End time must be greater than or equal to start time.')
      return
    }

    const alreadyExistsInOtherCategories = items.some(
      (item) =>
        (item.type === 'DOT_Hours' || item.type === 'DOT_Dispatch') &&
        item.orderType === selectedOrderType,
    )

    if (alreadyExistsInOtherCategories) {
      setCutoffError('SLA already configured for Order type.')
      return
    }

    const hasOverlap = isCutoffOverlapping(
      items,
      selectedOrderType,
      selectedStart,
      selectedEnd,
    )

    if (hasOverlap) {
      setCutoffError(
        'Cut-off time overlaps with an existing range for this order type.',
      )
      return
    }

    setCutoffError('')

    addItem({
      id: makeId(),
      type: 'DOT_Cutoff',
      orderType: selectedOrderType,
      startTime: selectedStart,
      endTime: selectedEnd,
      dispatchDays: parseInt(dispatchDays || '0', 10),
    })

    setCutoffOrderType(null)
    setCutoffStart(DEFAULT_CUTOFF_START)
    setCutoffEnd(DEFAULT_CUTOFF_END)
    setDispatchDays('0')
  }

  const handleAddDispatch = () => {
    if (!dispatchOrderType) return

    const selectedOrderType = getSelectValue(dispatchOrderType)

    if (!selectedOrderType || selectedOrderType === 'Select') {
      setDispatchError('Please select a valid order type.')
      return
    }

    if (selectedOrderType === ALL_ORDER_TYPE && hasSpecificConfiguration) {
      setDispatchError(
        'Please remove type specific configuration to add all type.',
      )
      return
    }

    if (selectedOrderType !== ALL_ORDER_TYPE && hasAllConfiguration) {
      setDispatchError(
        'Please remove all type configuration to add type specific',
      )
      return
    }
    const alreadyExistsInOtherCategories = items.some(
      (item) =>
        (item.type === 'DOT_Hours' || item.type === 'DOT_Cutoff') &&
        item.orderType === selectedOrderType,
    )

    if (alreadyExistsInOtherCategories) {
      setDispatchError('SLA already configured for Order type.')
      return
    }

    setDispatchError('')

    addItem({
      id: makeId(),
      type: 'DOT_Dispatch',
      orderType: selectedOrderType,
    })

    setDispatchOrderType(null)
  }

  return (
    <div className={`pt-10 ${className}`}>
      <p className='mb-2 font-semibold text-N-700'>
        Partner SLAs<span className='ml-[2px] text-R-500'>*</span>
      </p>

      <div className='space-y-8'>
        <SectionCard title='Based on hours'>
          <div className='mb-4 flex flex-wrap items-center gap-3'>
            <div className='w-[160px]'>
              <SimpleSelect
                value={hoursOrderType}
                onChange={(e: SelectOption | null) => {
                  setHoursError('')
                  setHoursOrderType(e)
                }}
                options={availableOrderTypes}
                placeholder='Order type'
                size='md'
              />
            </div>

            <Input
              placeholder='Hours'
              type='text'
              inputMode='numeric'
              className='w-[120px]'
              value={hoursValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const nextValue = e.target.value
                if (isNumericString(nextValue)) {
                  setHoursError('')
                  setHoursValue(nextValue)
                }
              }}
            />

            <Button
              className='px-4'
              onClick={handleAddHours}
              disabled={!hoursOrderType || hoursValue === ''}>
              Add
            </Button>
          </div>

          {hoursError && (
            <p className='mt-2 text-sm text-R-500'>{hoursError}</p>
          )}

          {hoursRows.length > 0 && (
            <>
              <div className='mb-2 grid grid-cols-[180px_120px_40px] gap-4 text-sm font-medium text-N-700'>
                <span>Order type</span>
                <span>Hours</span>
                <span />
              </div>

              <ul className='space-y-2'>
                {hoursRows.map((item) => (
                  <li
                    key={item.id}
                    className='grid grid-cols-[180px_120px_40px] items-center gap-4 text-sm'>
                    <span>{item.orderType}</span>
                    <span>{item.hours}</span>
                    <DeleteButton onClick={() => removeItem(item.id)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </SectionCard>

        <SectionCard title='Based on order cut-off time'>
          <div className='mb-4 flex flex-wrap items-center gap-3'>
            <div className='w-[160px]'>
              <SimpleSelect
                value={cutoffOrderType}
                onChange={(e: SelectOption | null) => {
                  setCutoffError('')
                  setCutoffOrderType(e)
                }}
                options={availableOrderTypes}
                placeholder='Order type'
                size='md'
              />
            </div>

            <div className='w-[150px]'>
              <SimpleSelect
                value={cutoffStart}
                onChange={(e: SelectOption) => {
                  setCutoffError('')
                  setCutoffStart(e)
                }}
                options={timeOptions}
                placeholder='Start'
                size='md'
              />
            </div>

            <div className='w-[150px]'>
              <SimpleSelect
                value={cutoffEnd}
                onChange={(e: SelectOption) => {
                  setCutoffError('')
                  setCutoffEnd(e)
                }}
                options={timeOptions}
                placeholder='End'
                size='md'
              />
            </div>

            <Input
              placeholder='Dispatch days'
              type='text'
              inputMode='numeric'
              className='w-[120px]'
              value={dispatchDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const nextValue = e.target.value
                if (isNumericString(nextValue)) {
                  setCutoffError('')
                  setDispatchDays(nextValue)
                }
              }}
            />

            <Button
              className='px-4'
              onClick={handleAddCutoff}
              disabled={!cutoffOrderType}>
              Add
            </Button>
          </div>

          {cutoffError && (
            <p className='mt-2 text-sm text-R-500'>{cutoffError}</p>
          )}

          {cutoffRows.length > 0 && (
            <>
              <div className='mb-2 mt-4 grid grid-cols-[100px_100px_100px_100px_100px_40px] gap-4 text-sm font-medium text-N-700'>
                <span>Order type</span>
                <span>Start</span>
                <span>End</span>
                <span>Dispatch in (Days)</span>
                <span />
                <span />
              </div>

              <ul className='space-y-2'>
                {cutoffRows.map((item) => (
                  <li
                    key={item.id}
                    className='grid grid-cols-[100px_100px_100px_100px_100px_40px] items-center gap-4 text-sm'>
                    <span>{item.orderType}</span>
                    <span>{item.startTime}</span>
                    <span>{item.endTime}</span>
                    <span>{item.dispatchDays}</span>
                    <span className='text-N-600'>
                      {formatUpdatedText(item)}
                    </span>
                    <DeleteButton onClick={() => removeItem(item.id)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </SectionCard>

        <SectionCard title='Based on customer dispatch date (Push API)'>
          <div className='mb-4 flex flex-wrap items-center gap-3'>
            <div className='w-[160px]'>
              <SimpleSelect
                value={dispatchOrderType}
                onChange={(e: SelectOption | null) => {
                  setDispatchError('')
                  setDispatchOrderType(e)
                }}
                options={availableOrderTypes}
                placeholder='Order type'
                size='md'
              />
            </div>

            <Button
              className='px-4'
              onClick={handleAddDispatch}
              disabled={!dispatchOrderType}>
              Add
            </Button>
          </div>

          {dispatchError && (
            <p className='mt-2 text-sm text-R-500'>{dispatchError}</p>
          )}

          {dispatchRows.length > 0 && (
            <>
              <div className='mb-2 grid grid-cols-[180px_40px] gap-4 text-sm font-medium text-N-700'>
                <span>Order type</span>
                <span />
              </div>

              <ul className='space-y-2'>
                {dispatchRows.map((item) => (
                  <li
                    key={item.id}
                    className='grid grid-cols-[180px_40px] items-center gap-4 text-sm'>
                    <span>{item.orderType}</span>
                    <DeleteButton onClick={() => removeItem(item.id)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  )
}

PartnerSLA.defaultProps = {
  className: '',
}

export default PartnerSLA
